import { Children, forwardRef, isValidElement } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "../lib/cn";
import { ChevronDownIcon } from "../lib/icons";

/**
 * Accordion — divider-separated expandable panels on
 * @radix-ui/react-accordion, mirroring MUI Accordion's default look.
 *
 *   <Accordion>
 *     <AccordionItem value="details" title="Booking details" defaultOpen>
 *       Everything the office needs to approve…
 *     </AccordionItem>
 *     <AccordionItem value="history" title="Approval history">
 *       …
 *     </AccordionItem>
 *   </Accordion>
 *
 * Items open independently by default (MUI behaviour); pass `multiple={false}`
 * for a single-open list. `AccordionItem` is the prop-driven panel; the
 * `AccordionTrigger`/`AccordionContent` exports are the styled building
 * blocks for custom item layouts.
 */

/** Collect the values of AccordionItem children marked defaultOpen. */
function collectDefaultOpen(children: React.ReactNode): string[] {
  return Children.toArray(children).flatMap((child) => {
    if (
      !isValidElement(child) ||
      child.type !== AccordionItem ||
      !(child.props as AccordionItemProps).defaultOpen
    ) {
      return [];
    }
    return [(child.props as AccordionItemProps).value];
  });
}

export interface AccordionProps {
  /** Values open on first render; defaults to items marked defaultOpen. */
  defaultValue?: string[];
  /** Called with the values of the open items whenever they change. */
  onValueChange?: (value: string[]) => void;
  /** Allow more than one item open at a time (default, MUI-like). */
  multiple?: boolean;
  /** Disables every item. */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    { defaultValue, onValueChange, multiple = true, disabled, className, children },
    ref,
  ) => {
    const defaults = defaultValue ?? collectDefaultOpen(children);
    const shared = {
      ref,
      disabled,
      "data-slot": "accordion",
      className: cn(
        "divide-y divide-border-default overflow-hidden rounded-xl border border-border-default bg-surface-default",
        className,
      ),
      children,
    } as const;
    if (multiple) {
      return (
        <AccordionPrimitive.Root
          type="multiple"
          defaultValue={defaults}
          onValueChange={onValueChange}
          {...shared}
        />
      );
    }
    return (
      <AccordionPrimitive.Root
        type="single"
        collapsible
        defaultValue={defaults[0]}
        onValueChange={(value) => {
          onValueChange?.(value ? [value] : []);
        }}
        {...shared}
      />
    );
  },
);
Accordion.displayName = "Accordion";

export interface AccordionItemProps {
  /** Panel identifier — surfaces in open-value callbacks. */
  value: string;
  /** Header text beside the chevron. */
  title: React.ReactNode;
  /** Opens the panel on first render (collected by the root). */
  defaultOpen?: boolean;
  /** Disables just this panel's trigger. */
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const AccordionItem = forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ value, title, disabled, children, className }, ref) => {
  return (
    <AccordionPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      data-slot="accordion-item"
      className={className}
    >
      <AccordionPrimitive.Header className="flex">
        <AccordionTrigger>{title}</AccordionTrigger>
      </AccordionPrimitive.Header>
      <AccordionContent>{children}</AccordionContent>
    </AccordionPrimitive.Item>
  );
});
AccordionItem.displayName = "AccordionItem";

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {}

/** Header control — title on the left, chevron rotating as it opens. */
export const AccordionTrigger = forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, ...props }, ref) => {
  return (
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "group flex flex-1 cursor-default select-none items-center justify-between gap-2 px-4 py-3 text-left type-body-md font-medium text-content-primary outline-none transition-colors",
        "hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-inset",
        "disabled:pointer-events-none disabled:opacity-38",
        className,
      )}
      {...props}
    >
      <span className="min-w-0">{children}</span>
      <ChevronDownIcon
        aria-hidden="true"
        className="size-4 shrink-0 text-content-secondary transition-transform duration-200 group-data-[state=open]:rotate-180"
      />
    </AccordionPrimitive.Trigger>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

export interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {}

/**
 * Expandable body — height animates against Radix's measured
 * --radix-accordion-content-height (keyframes in styles/index.css).
 */
export const AccordionContent = forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, ...props }, ref) => {
  return (
    <AccordionPrimitive.Content
      ref={ref}
      className={cn(
        "overflow-hidden data-[state=closed]:animate-accordion-content-closed data-[state=open]:animate-accordion-content-open",
        className,
      )}
      {...props}
    >
      <div className="px-4 pb-4 type-body-md text-content-secondary">{children}</div>
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = "AccordionContent";
